import { Label } from "@/components/ui/label";
import * as Icons from "lucide-react";

import DynamicIcon from "@/components/utils/lucide-icon";
import { registerFieldLabel } from "@/hooks/useFormValidation";
import { ReactNode, useContext, useEffect } from "react";
import { FormContext } from "./form-dialog";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import { Info, Lock } from "lucide-react";

type IconName = keyof typeof Icons;

// ========== FIELD WRAPPER ==========
type FormFieldProps = {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: ReactNode;
  helperText?: string;
  error?: string;
  icon?: IconName;
  tooltip?: string;
  showErrorAsTooltip?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  pattern?: string;
  isLoading?: boolean;
};

export function FormField({
  label,
  htmlFor,
  required = false,
  children,
  helperText,
  error,
  icon,
  tooltip,
  showErrorAsTooltip = true,
  disabled,
  hidden,
  pattern,
  isLoading,
}: FormFieldProps) {
  // Obtener formId del contexto automáticamente
  const { formId } = useContext(FormContext);

  // Registrar el label automáticamente cuando el componente se monta
  useEffect(() => {
    registerFieldLabel(htmlFor, label, formId);
  }, [htmlFor, label, formId]);

  // helperText se convierte en tooltip por defecto
  // Si hay error y showErrorAsTooltip está activo, el error tiene prioridad
  const tooltipText =
    showErrorAsTooltip && error ? error : tooltip || helperText;

  // Determinar color del tooltip según si es error o info
  const tooltipIconColor =
    showErrorAsTooltip && error
      ? "text-yellow-600 hover:text-yellow-700 dark:text-yellow-500 dark:hover:text-yellow-600"
      : "text-muted-foreground hover:text-foreground";

  return (
    <div
      className={`space-y-2 ${isLoading && "animate-pulse"}`}
      hidden={hidden}
    >
      <Label
        htmlFor={htmlFor}
        className={cn(
          "flex items-center",
          tooltipText ? "justify-between" : "gap-2",
        )}
      >
        <span className="flex items-center gap-2">
          {icon && <DynamicIcon name={icon} className="w-4 h-4" />}
          {label}
          {required && !tooltipText && (
            <Lock className="h-3 w-3 text-primary" />
          )}
        </span>
        {tooltipText && (
          <span className="flex items-center gap-1">
            {required && <Lock className="h-3 w-3 text-primary" />}
            <Tooltip>
              <TooltipTrigger
                type="button"
                tabIndex={-1}
                className="inline-flex cursor-help"
              >
                <Info
                  className={cn("w-4 h-4 transition-colors", tooltipIconColor)}
                />
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs z-[9999]"
                sideOffset={5}
              >
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </span>
        )}
      </Label>
      {children}
      {!showErrorAsTooltip && error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}
